import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';

const slides = [
  // TITRE
  {
    id: 1, type: 'title',
    title: "Glossaire Professionnel Officiel",
    subtitle: "DEEJE - Accueil et accompagnement du jeune enfant et de sa famille"
  },
  // A (1/2)
  {
    id: 2, type: 'glossary', letter: 'A',
    terms: [
      { term: "Accompagnement individualisé", definition: "Démarche éducative consistant à adapter les pratiques professionnelles aux besoins, aux capacités, au rythme et à la singularité de chaque enfant tout en tenant compte du cadre collectif." },
      { term: "Adaptation / Familiarisation", definition: "Période progressive permettant à l'enfant et à sa famille de découvrir la structure. Elle vise à sécuriser la séparation, instaurer un lien de confiance et recueillir les habitudes et besoins spécifiques de l'enfant." },
      { term: "Adulte phare", definition: "Professionnel repère stable et sécurisant au sein du groupe. Il constitue un point d'appui relationnel favorisant l'exploration autonome tout en garantissant une présence contenante et rassurante." },
      { term: "Alliance éducative", definition: "Relation de coopération construite entre professionnels et familles autour du développement de l'enfant, favorisant la continuité éducative." }
    ]
  },
  // A (2/2)
  {
    id: 3, type: 'glossary', letter: 'A',
    terms: [
      { term: "Altérité", definition: "Reconnaissance de l'autre comme sujet distinct, porteur d'émotions, de besoins et d'une identité propre." },
      { term: "Analyse réflexive", definition: "Démarche professionnelle consistant à questionner sa pratique afin d'en comprendre les enjeux et d'en ajuster les modalités." },
      { term: "Autonomisation", definition: "Processus progressif par lequel l'enfant développe sa capacité à agir par lui-même dans un cadre sécurisant et contenant." }
    ]
  },
  // B
  {
    id: 4, type: 'glossary', letter: 'B',
    terms: [
      { term: "Base de sécurité", definition: "Figure stable et rassurante permettant à l'enfant d'explorer son environnement tout en se sentant protégé." },
      { term: "Bientraitance", definition: "Démarche active visant à garantir le respect, la dignité et le bien-être de l'enfant dans toutes les pratiques professionnelles." }
    ]
  },
  // C (1/3)
  {
    id: 5, type: 'glossary', letter: 'C',
    terms: [
      { term: "Cadre sécurisant", definition: "Ensemble de repères spatiaux, temporels et relationnels assurant stabilité et prévisibilité pour l'enfant." },
      { term: "Climat de confiance", definition: "Atmosphère relationnelle favorisant l'expression des émotions et la coopération entre enfants, familles et professionnels." },
      { term: "Cohérence éducative", definition: "Harmonisation des pratiques entre professionnels et familles afin d'assurer une continuité éducative." }
    ]
  },
  // C (2/3)
  {
    id: 6, type: 'glossary', letter: 'C',
    terms: [
      { term: "Cohésion d'équipe", definition: "Capacité d'un collectif professionnel à travailler de manière harmonieuse autour d'objectifs communs dans un climat de respect et de coopération." },
      { term: "Communication Non Violente (CNV)", definition: "Méthode relationnelle reposant sur l'expression des faits, des émotions, des besoins et des demandes afin de favoriser la coopération." },
      { term: "Communication positive", definition: "Approche éducative fondée sur la valorisation, l'encouragement et la reformulation constructive, visant à soutenir l'estime de soi et la coopération." }
    ]
  },
  // C (3/3)
  {
    id: 7, type: 'glossary', letter: 'C',
    terms: [
      { term: "Continuité relationnelle", definition: "Stabilité des figures adultes référentes favorisant la sécurité affective de l'enfant." },
      { term: "Convention", definition: "Document formalisé définissant les engagements réciproques entre structures partenaires dans le cadre d'un projet commun." },
      { term: "Co-éducation", definition: "Collaboration active entre professionnels et familles dans l'accompagnement éducatif de l'enfant." }
    ]
  },
  // D
  {
    id: 8, type: 'glossary', letter: 'D',
    terms: [
      { term: "Développement global", definition: "Évolution simultanée et interdépendante des dimensions motrices, cognitives, affectives, sociales et langagières." },
      { term: "Distanciation professionnelle", definition: "Capacité à prendre du recul afin d'analyser objectivement une situation tout en respectant l'éthique professionnelle." },
      { term: "Documentation pédagogique", definition: "Recueil structuré d'observations et d'analyses permettant d'évaluer et d'ajuster les pratiques éducatives." },
      { term: "Douces violences", definition: "Gestes, paroles ou attitudes inadaptés, souvent involontaires, pouvant fragiliser l'enfant (imposer un rythme, ne pas prévenir avant un soin, parler de lui sans l'inclure). Leur prévention suppose vigilance et réflexion éthique." }
    ]
  },
  // E
  {
    id: 9, type: 'glossary', letter: 'E',
    terms: [
      { term: "Empathie", definition: "Capacité à comprendre et accueillir les émotions d'autrui sans jugement." },
      { term: "Environnement préparé", definition: "Organisation réfléchie de l'espace favorisant autonomie, exploration et sécurité." },
      { term: "Étayage", definition: "Soutien ajusté et progressif apporté à l'enfant pour favoriser l'acquisition de nouvelles compétences." },
      { term: "Évaluation", definition: "Analyse de l'efficacité d'une action éducative au regard des objectifs fixés et des besoins identifiés." }
    ]
  },
  // F
  {
    id: 10, type: 'glossary', letter: 'F',
    terms: [
      { term: "Figure d'attachement", definition: "Personne significative auprès de laquelle l'enfant développe un lien affectif stable et sécurisant. En collectivité, un professionnel peut devenir une figure d'attachement secondaire si la continuité relationnelle est assurée." },
      { term: "Fonction parentale", definition: "Capacité à répondre aux besoins affectifs, éducatifs et structurants de l'enfant." }
    ]
  },
  // H
  {
    id: 11, type: 'glossary', letter: 'H',
    terms: [
      { term: "Holding", definition: "Portage physique et psychique garantissant sécurité et continuité affective." },
      { term: "Handling", definition: "Manière respectueuse et contenante de manipuler l'enfant lors des soins." }
    ]
  },
  // I
  {
    id: 12, type: 'glossary', letter: 'I',
    terms: [
      { term: "Inclusion", definition: "Principe visant à permettre à chaque enfant, quelles que soient ses particularités, de participer pleinement à la vie collective." },
      { term: "Information préoccupante (IP)", definition: "Transmission officielle d'éléments laissant supposer qu'un enfant est en danger ou en risque de danger." }
    ]
  },
  // J
  {
    id: 13, type: 'glossary', letter: 'J',
    terms: [
      { term: "Jeu libre", definition: "Activité spontanée initiée par l'enfant, favorisant exploration et créativité." },
      { term: "Jeu symbolique", definition: "Activité permettant la représentation imaginaire et l'élaboration psychique." }
    ]
  },
  // L + M
  {
    id: 14, type: 'glossary', letter: 'L - M',
    terms: [
      { term: "Lien de confiance", definition: "Relation progressive fondée sur la fiabilité, la transparence et le respect mutuel entre enfant, famille et professionnels." },
      { term: "Médiation", definition: "Intervention facilitant communication, régulation ou apprentissage à l'aide d'un support relationnel ou éducatif." },
      { term: "Motricité libre", definition: "Approche respectant le développement moteur spontané sans position imposée." }
    ]
  },
  // O
  {
    id: 15, type: 'glossary', letter: 'O',
    terms: [
      { term: "Observation professionnelle", definition: "Recueil structuré et factuel d'informations permettant une analyse éducative." },
      { term: "Observation-projet", definition: "Observation orientée vers un objectif d'ajustement des pratiques." },
      { term: "Objet transitionnel", definition: "Objet investi affectivement facilitant la séparation et la régulation émotionnelle." }
    ]
  },
  // P (1/2)
  {
    id: 16, type: 'glossary', letter: 'P',
    terms: [
      { term: "Parentalité", definition: "Ensemble des responsabilités et compétences liées à l'exercice du rôle parental." },
      { term: "Partenaire", definition: "Acteur institutionnel collaborant dans une dynamique de co-construction autour d'un projet éducatif ou social." },
      { term: "Partenariat", definition: "Collaboration structurée entre différents acteurs autour d'objectifs communs." },
      { term: "Permanence de l'objet", definition: "Compréhension que l'objet ou la personne continue d'exister même en son absence." }
    ]
  },
  // P (2/2)
  {
    id: 17, type: 'glossary', letter: 'P',
    terms: [
      { term: "Posture professionnelle", definition: "Attitude éthique, réflexive et ajustée adoptée dans l'exercice des missions." },
      { term: "Prestataire", definition: "Intervenant extérieur fournissant un service ponctuel sans s'inscrire dans une dynamique partenariale durable." },
      { term: "Prévention précoce", definition: "Actions visant à repérer et intervenir le plus tôt possible face aux difficultés potentielles." },
      { term: "Projet éducatif", definition: "Document institutionnel définissant les valeurs et orientations pédagogiques d'une structure." }
    ]
  },
  // R
  {
    id: 18, type: 'glossary', letter: 'R',
    terms: [
      { term: "Référence éducative", definition: "Organisation désignant un professionnel comme interlocuteur privilégié pour un enfant et sa famille." },
      { term: "Régulation émotionnelle", definition: "Capacité à identifier, comprendre et moduler ses émotions avec l'aide d'un adulte contenant." },
      { term: "Résilience", definition: "Processus de reconstruction psychique après une épreuve, soutenu par des facteurs protecteurs." },
      { term: "Réseau", definition: "Ensemble structuré d'acteurs institutionnels mobilisés autour d'objectifs communs sur un territoire." }
    ]
  },
  // S
  {
    id: 19, type: 'glossary', letter: 'S',
    terms: [
      { term: "Sécurité affective", definition: "Sentiment interne de protection résultant de relations stables, prévisibles et contenantes." },
      { term: "Singularité", definition: "Reconnaissance de l'unicité de chaque enfant." },
      { term: "Socialisation", definition: "Processus d'apprentissage progressif des règles et interactions sociales." },
      { term: "Système de référence", definition: "Organisation institutionnelle garantissant la continuité relationnelle et l'individualisation de l'accompagnement par la désignation d'un professionnel référent." }
    ]
  },
  // V + Z
  {
    id: 20, type: 'glossary', letter: 'V - Z',
    terms: [
      { term: "Veille professionnelle", definition: "Démarche continue d'actualisation des connaissances législatives, pédagogiques et scientifiques permettant d'adapter les pratiques professionnelles aux évolutions du secteur." },
      { term: "Zone Prochaine de Développement (ZPD)", definition: "Espace situé entre ce que l'enfant peut faire seul et ce qu'il peut accomplir avec l'aide d'un adulte ou d'un pair." }
    ]
  },
  // FÉLICITATIONS
  {
    id: 21, type: 'congrats',
    title: "Glossaire terminé !",
    message: "Vous disposez désormais d'un vocabulaire professionnel complet pour rédiger votre Livret 2 et enrichir vos écrits avec les termes officiels du DEEJE.",
    cta: "Utilisez ces termes dans vos situations professionnelles pour structurer vos analyses"
  },
];

const ACCENT = '#0D9488';
const ACCENT_LIGHT = '#0D948811';
const ACCENT_BORDER = '#0D948820';
const ACCENT_BG = '#0D948808';

const DEEJEGlossairePresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const synthRef = useRef(window.speechSynthesis);

  const slide = slides[currentSlide];
  const total = slides.length;

  const goNext = () => { if (currentSlide < total - 1) setCurrentSlide(currentSlide + 1); stopSpeech(); };
  const goPrev = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); stopSpeech(); };

  const stopSpeech = () => { synthRef.current.cancel(); setIsSpeaking(false); };
  const toggleSpeech = () => {
    if (isSpeaking) { stopSpeech(); return; }
    let text = '';
    if (slide.terms) {
      text = slide.terms.map(t => `${t.term} : ${t.definition}`).join('. ');
    } else {
      text = slide.message || slide.subtitle || '';
    }
    if (!text) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'fr-FR';
    utterance.onend = () => setIsSpeaking(false);
    synthRef.current.speak(utterance);
    setIsSpeaking(true);
  };

  const progress = ((currentSlide + 1) / total) * 100;

  const renderSlide = () => {
    switch (slide.type) {
      case 'title':
        return (
          <div data-testid="glos-slide-title" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#128218;</div>
            <h1 style={{ fontSize: '2rem', fontWeight: '700', color: ACCENT, marginBottom: '1rem' }}>{slide.title}</h1>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>{slide.subtitle}</p>
            <div style={{ marginTop: '2rem', padding: '1rem', background: ACCENT_LIGHT, borderRadius: '0.75rem', maxWidth: '500px', margin: '2rem auto 0' }}>
              <p style={{ color: '#555', fontSize: '0.95rem' }}>56 termes professionnels essentiels pour le DEEJE, classés par ordre alphabétique.</p>
            </div>
          </div>
        );
      case 'glossary':
        return (
          <div data-testid="glos-slide-glossary" style={{ padding: '1.5rem 2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '0.75rem', background: `linear-gradient(135deg, ${ACCENT}, #14B8A6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.5rem', fontWeight: '800', flexShrink: 0 }}>
                {slide.letter}
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333' }}>Lettre {slide.letter}</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {slide.terms.map((t, i) => (
                <div key={i} style={{ background: 'white', border: `1px solid ${ACCENT_BORDER}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', borderLeft: `4px solid ${ACCENT}` }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '700', color: ACCENT, marginBottom: '0.4rem' }}>{t.term}</h4>
                  <p style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#555', margin: 0 }}>{t.definition}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'congrats':
        return (
          <div data-testid="glos-slide-congrats" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#127891;</div>
            <h2 style={{ fontSize: '1.8rem', fontWeight: '700', color: ACCENT, marginBottom: '1rem' }}>{slide.title}</h2>
            <p style={{ lineHeight: '1.7', color: '#555', maxWidth: '600px', margin: '0 auto 1.5rem' }}>{slide.message}</p>
            <p style={{ color: ACCENT, fontWeight: '600' }}>{slide.cta}</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '0.5rem' }}>
        <div style={{ width: `${progress}%`, height: '100%', background: `linear-gradient(90deg, ${ACCENT}, #14B8A6)`, borderRadius: '4px', transition: 'width 0.3s' }} />
      </div>
      <div className="card" style={{ minHeight: '400px', margin: 0 }}>
        {renderSlide()}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', marginTop: '0.5rem', borderTop: '1px solid #eee' }}>
        <button data-testid="glos-prev-btn" onClick={goPrev} disabled={currentSlide === 0}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', border: `1px solid ${ACCENT}`, borderRadius: '0.5rem', background: 'white', color: ACCENT, cursor: currentSlide === 0 ? 'not-allowed' : 'pointer', opacity: currentSlide === 0 ? 0.4 : 1, fontWeight: '500', fontSize: '0.9rem' }}>
          <ChevronLeft size={16} /> Précédent
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={toggleSpeech}
            style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '50%', background: ACCENT, color: 'white', cursor: 'pointer' }}>
            {isSpeaking ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span style={{ fontWeight: '700', fontSize: '0.9rem', color: ACCENT }}>{currentSlide + 1} / {total}</span>
          <span style={{ fontSize: '0.8rem', color: '#999' }}>{Math.round(progress)}%</span>
        </div>
        <button data-testid="glos-next-btn" onClick={goNext} disabled={currentSlide === total - 1}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', border: 'none', borderRadius: '0.5rem', background: ACCENT, color: 'white', cursor: currentSlide === total - 1 ? 'not-allowed' : 'pointer', opacity: currentSlide === total - 1 ? 0.4 : 1, fontWeight: '500', fontSize: '0.9rem' }}>
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default DEEJEGlossairePresentation;
